import { useState, useCallback, useEffect, useRef, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { api } from '../../../../services/api'
import { useQuery } from 'react-query'
import { useForm } from 'react-hook-form'
import { addDays, format } from 'date-fns'

export const useMensagensAgendadas = (toast) => {

  const [searchParams, setSearchParams] = useSearchParams()
  const isUpdatingFromUrl = useRef(false)
  const isMounted = useRef(false)
  const hasInitialFetch = useRef(false)

  // Usar useMemo para evitar recriação em cada renderização
  const defaultDates = useMemo(() => {
    const today = new Date()
    const tenDaysLater = addDays(today, 10)

    return {
      inicio: format(today, 'yyyy-MM-dd'),
      fim: format(tenDaysLater, 'yyyy-MM-dd'),
    }
  }, [])

  // Usar useMemo para valores iniciais dos filtros
  const initialFilterValues = useMemo(() => {
    return {
      cod_parceiro: searchParams.get('cod_parceiro') || '',
      cod_vendedor: searchParams.get('cod_vendedor') || '',
      nome_vendedor: searchParams.get('nome_vendedor') || '',
      data_agendamento_inicio: searchParams.get('data_agendamento_inicio') || '',
      data_agendamento_fim: searchParams.get('data_agendamento_fim') || '',
      tipo_agendamento: searchParams.get('tipo_agendamento') || '',
      status: searchParams.get('status') || 'pendentes',
      filtrar_por: searchParams.get('filtrar_por') || '', //adicionado filtrar_por
      ordenacao: searchParams.get('ordenacao') || '', // adicionado ordenacao
    }
  }, [searchParams])

  const { handleSubmit, register, reset, setValue } = useForm({
    defaultValues: initialFilterValues,
  })

  const [filtros, setFiltros] = useState(initialFilterValues)

  const [paginacao, setPaginacao] = useState({
    pagina: parseInt(searchParams.get('pagina')) || 1,
    limite: parseInt(searchParams.get('limite')) || 10,
    pagina_atual: parseInt(searchParams.get('pagina')) || 1,
    ultima_pagina: 1,
    total: 0,
    por_pagina: parseInt(searchParams.get('limite')) || 10,
  })



  const {
    data: agendamentosData,
    isLoading: isLoadingAgendamentos,
    isRefetching: isRefetchingAgendamentos,
    refetch: refetchAgendamentos,
    isError,
  } = useQuery(
    ['agendamentos-mensagens', filtros, paginacao.pagina, paginacao.limite],
    async () => {
      const params = {
        page: paginacao.pagina,
        limit: paginacao.limite,
      }

      let endpoint = 'https://api.innovationbrindes.com.br/api/site/v2/orcamento-crm/listar-agendamentos-vendedor/0?filtrar_por=contato&ordenacao=asc'

      if (filtros.cod_vendedor) {
        endpoint = `/site/v2/orcamento-crm/listar-agendamentos-vendedor/${filtros.cod_vendedor}`
      } 

      if (filtros.cod_parceiro) {
        params.cod_parceiro = filtros.cod_parceiro
      }
      
      if (filtros.nome_vendedor) {
        params.nome_vendedor = filtros.nome_vendedor
      }

      if (filtros.data_agendamento_inicio) {
        params.data_agendamento_inicio = filtros.data_agendamento_inicio
      }

      if (filtros.data_agendamento_fim) {
        params.data_agendamento_fim = filtros.data_agendamento_fim
      }

      if (filtros.tipo_agendamento) {
        params.tipo_agendamento = filtros.tipo_agendamento
      }

      if (filtros.status) {
        params.status = filtros.status
      }

      if (filtros.filtrar_por) {
        params.filtrar_por = filtros.filtrar_por
      }

      if (filtros.ordenacao) {
        params.ordenacao = filtros.ordenacao
      }

      const response = await api.get(endpoint, { params })

      // A resposta agora tem o formato { agendamentos: [...], paginacao: {...} }
      const { agendamentos = [], paginacao: paginacaoAPI = {} } = response.data || {}

      // Garantir que mensagem_escolhida exista e seja tratada como texto simples
      const processedAgendamentos = agendamentos.map((agendamento) => ({
        ...agendamento,
        mensagem_escolhida: agendamento.mensagem_escolhida || '',
        status: agendamento.status || 'pendente',
        tipo_agendamento: agendamento.tipo_agendamento || '',
      }))

      return {
        agendamentos: processedAgendamentos,
        paginacao: {
          total: paginacaoAPI.total || 0,
          por_pagina: paginacaoAPI.por_pagina || params.limit,
          pagina_atual: paginacaoAPI.pagina_atual || params.page,
          ultima_pagina: paginacaoAPI.ultima_pagina || 1,
          proxima_pagina_url: paginacaoAPI.proxima_pagina_url || null,
          pagina_anterior_url: paginacaoAPI.pagina_anterior_url || null,
        },
      }
    },
    {
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      retry: 1,
      enabled: false, // Desabilitar o fetch automático
      onSuccess: (data) => {
        // Atualizar a paginação com os valores retornados da API
        setPaginacao((prev) => ({
          ...prev,
          total: data.paginacao.total,
          ultima_pagina: data.paginacao.ultima_pagina,
          pagina_atual: data.paginacao.pagina_atual,
          por_pagina: data.paginacao.por_pagina,
        }))
      },
      onError: (error) => {
        console.error('Erro ao buscar agendamentos:', error)
        toast({
          title: 'Erro',
          description: error.response?.data?.message || 'Não foi possível carregar os agendamentos.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        })
      },
    }
  )

  // Efeito para atualizar a URL quando os filtros ou paginação mudam
  useEffect(() => {
    if (!isMounted.current) {
      return
    }

    if (isUpdatingFromUrl.current) {
      return
    }

    const params = new URLSearchParams()

    if (filtros.cod_parceiro) {
      params.set('cod_parceiro', filtros.cod_parceiro)
    }

    if (filtros.cod_vendedor) {
      params.set('cod_vendedor', filtros.cod_vendedor)
    }

    if (filtros.nome_vendedor) {
      params.set('nome_vendedor', filtros.nome_vendedor)
    }

    if (filtros.data_agendamento_inicio) {
      params.set('data_agendamento_inicio', filtros.data_agendamento_inicio)
    }

    if (filtros.data_agendamento_fim) {
      params.set('data_agendamento_fim', filtros.data_agendamento_fim)
    }

    if (filtros.tipo_agendamento) {
      params.set('tipo_agendamento', filtros.tipo_agendamento)
    }

    if (filtros.status) {
      params.set('status', filtros.status)
    }

    if (paginacao.pagina !== 1) {
      params.set('pagina', paginacao.pagina.toString())
    }

    if (paginacao.limite !== 10) {
      params.set('limite', paginacao.limite.toString())
    }
    if (filtros.filtrar_por) {
      params.set('filtrar_por', filtros.filtrar_por)
    }

    if (filtros.ordenacao) {
      params.set('ordenacao', filtros.ordenacao)
    }

    setSearchParams(params, { replace: true })
  }, [filtros, paginacao.pagina, paginacao.limite, setSearchParams])

  // Efeito para inicialização e atualização de valores da URL
  useEffect(() => {
    const shouldFetch = !isMounted.current || !hasInitialFetch.current

    if (!isMounted.current) {
      isMounted.current = true
    }

    if (shouldFetch) {
      hasInitialFetch.current = true
      refetchAgendamentos()
    }
  }, [refetchAgendamentos])

  // Efeito para atualizar os filtros quando a URL muda
  useEffect(() => {
    if (!isMounted.current) {
      return
    }

    isUpdatingFromUrl.current = true

    try {
      const urlCodParceiro = searchParams.get('cod_parceiro') || ''
      const urlCodVendedor = searchParams.get('cod_vendedor') || ''
      const urlNomeVendedor = searchParams.get('nome_vendedor') || ''
      const urlDataInicio = searchParams.get('data_agendamento_inicio') || ''
      const urlDataFim = searchParams.get('data_agendamento_fim') || ''
      const urlTipoAgendamento = searchParams.get('tipo_agendamento') || ''
      const urlStatus = searchParams.get('status') || 'pendentes'
      const urlPagina = searchParams.get('pagina') ? parseInt(searchParams.get('pagina')) : 1
      const urlLimite = searchParams.get('limite') ? parseInt(searchParams.get('limite')) : 10
      const urlFiltrarPor = searchParams.get('filtrar_por') || ''
      const urlOrdenacao = searchParams.get('ordenacao') || ''


      // Atualizar os valores do formulário sem causar re-render
      setValue('cod_parceiro', urlCodParceiro)
      setValue('cod_vendedor', urlCodVendedor)
      setValue('nome_vendedor', urlNomeVendedor)
      setValue('data_agendamento_inicio', urlDataInicio)
      setValue('data_agendamento_fim', urlDataFim)
      setValue('tipo_agendamento', urlTipoAgendamento)
      setValue('status', urlStatus)
      setValue('filtrar_por', urlFiltrarPor)
      setValue('ordenacao', urlOrdenacao)

      // Atualizar filtros aplicados
      setFiltros({
        cod_parceiro: urlCodParceiro,
        cod_vendedor: urlCodVendedor,
        nome_vendedor: urlNomeVendedor,
        data_agendamento_inicio: urlDataInicio,
        data_agendamento_fim: urlDataFim,
        tipo_agendamento: urlTipoAgendamento,
        status: urlStatus,
        filtrar_por: urlFiltrarPor,
        ordenacao: urlOrdenacao,
      })

      setPaginacao((prev) => ({
        ...prev,
        pagina: urlPagina,
        pagina_atual: urlPagina,
        limite: urlLimite,
      }))

      // Não disparamos refetch aqui para evitar loop
    } finally {
      setTimeout(() => {
        isUpdatingFromUrl.current = false
      }, 0)
    }
  }, [searchParams, setValue, defaultDates])

  const onSubmit = useCallback(
    (data) => {
      if (isUpdatingFromUrl.current) return

      setFiltros({
        cod_parceiro: data.cod_parceiro || '',
        cod_vendedor: data.cod_vendedor || '',
        nome_vendedor: data.nome_vendedor || '',
        data_agendamento_inicio: data.data_agendamento_inicio || '',
        data_agendamento_fim: data.data_agendamento_fim || '',
        tipo_agendamento: data.tipo_agendamento || '',
        status: data.status || '',
        filtrar_por: data.filtrar_por || '',
        ordenacao: data.ordenacao || '',
      })

      setPaginacao((prev) => ({
        ...prev,
        pagina: 1,
        pagina_atual: 1,
      }))

      setTimeout(() => {
        refetchAgendamentos()
      }, 0)
    },
    [refetchAgendamentos]
  )

  const handleLimparFiltros = useCallback(() => {
    if (isUpdatingFromUrl.current) return

    reset({
      cod_parceiro: '',
      cod_vendedor: '',
      nome_vendedor: '',
      data_agendamento_inicio: '',
      data_agendamento_fim: '',
      tipo_agendamento: '',
      status: 'pendentes',
    })

    setFiltros({
      cod_parceiro: '',
      cod_vendedor: '',
      nome_vendedor: '',
      data_agendamento_inicio: '',
      data_agendamento_fim: '',
      tipo_agendamento: '',
      status: 'pendentes',
      filtrar_por: '',
      ordenacao: '',

    })

    setPaginacao((prev) => ({
      ...prev,
      pagina: 1,
      pagina_atual: 1,
    }))

    setTimeout(() => {
      refetchAgendamentos()
    }, 0)
  }, [reset, refetchAgendamentos, defaultDates])

  const handleChangePaginacao = useCallback(
    (novaPagina) => {
      if (isUpdatingFromUrl.current) return

      setPaginacao((prev) => ({
        ...prev,
        pagina: novaPagina,
        pagina_atual: novaPagina,
      }))

      setTimeout(() => {
        refetchAgendamentos()
      }, 0)
    },
    [refetchAgendamentos]
  )

  const handleChangeLimite = useCallback(
    (novoLimite) => {
      if (isUpdatingFromUrl.current) return

      setPaginacao((prev) => ({
        ...prev,
        pagina: 1,
        pagina_atual: 1,
        limite: novoLimite,
        por_pagina: novoLimite,
      }))

      setTimeout(() => {
        refetchAgendamentos()
      }, 0)
    },
    [refetchAgendamentos]
  )
  const toggleOrdenacao = useCallback((campo) => {
    if (isUpdatingFromUrl.current) return

    setFiltros((prev) => {
      const ordenacaoAtual = prev.ordenacao === 'desc' ? 'asc' : 'desc'
      return {
        ...prev,
        filtrar_por: campo,
        ordenacao: prev.filtrar_por === campo ? ordenacaoAtual : 'desc', // se trocar campo, começa desc
      }
    })

    setPaginacao((prev) => ({
      ...prev,
      pagina: 1,
      pagina_atual: 1,
    }))

    setTimeout(() => {
      refetchAgendamentos()
    }, 0)
  }, [refetchAgendamentos])

  return {
    agendamentos: agendamentosData?.agendamentos || [],
    filtros,
    isLoading: isLoadingAgendamentos || isRefetchingAgendamentos,
    paginacao,
    register,
    handleSubmit: handleSubmit(onSubmit),
    handleChangePaginacao,
    handleChangeLimite,
    handleBuscarAgendamentos: useCallback(() => handleSubmit(onSubmit)(), [handleSubmit, onSubmit]),
    handleLimparFiltros,
    isError,
    toggleOrdenacao,
  }
}
